import api from "@/services/utils/apiInstance.ts";
import { buildNoSearchQuery } from "@/services/utils/request.ts";

import type {
  AssetDetailResponseApi,
  MandateTradingDataResponseApi,
  OrderHistoryRow,
  OrderHistoryRowResponseApi
} from "@/types/services/mandate/tradingMandate.type.ts";
import type { PaginatedNoSearchQuery } from "@/types/services/utils/request.type.ts";
import type { PaginatedResponse } from "@/types/services/utils/response.type.ts";

class TradingMandateService {
  async getMandateTradingData(mandateId: number): Promise<MandateTradingDataResponseApi> {
    const response = await api.get(`/trading/${mandateId}`)
    return {
      mandateName: response.data.mandate_name,
      publicStatus: response.data.public_status,
      assets: response.data.assets.map((asset: AssetDetailResponseApi) => ({
        name: asset.name,
        amount: asset.amount,
        currentRate: asset.current_rate,
        percentage: asset.percentage,
        mandatePercentage: asset.mandate_percentage
      }))
    }
  }

  async getPaginatedTradingHistory(mandateId: number, params: PaginatedNoSearchQuery): Promise<PaginatedResponse<OrderHistoryRow>> {
    const query: string = buildNoSearchQuery(params)
    const response = await api.get(`/trading/${mandateId}/history?${query.toString()}`);
    return {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      maxPages: response.data.max_pages,
      data: response.data.data.map((row: OrderHistoryRowResponseApi) => ({
        time: row.time,
        closingTime: row.closing_time,
        orderId: row.order_id,
        status: row.status,
        type: row.type,
        side: row.side,
        total: row.total,
        requestedPrice: row.requested_price,
        assetName: row.asset_name
      }))
    }
  }

  async placeMarketOrder(
    mandateId: number,
    fromAsset: string,
    amount: number,
    toAsset: string,
  ): Promise<MandateTradingDataResponseApi> {
    const response = await api.post(`/trading/${mandateId}/market-order`, {
      fromAsset: fromAsset,
      amount: amount,
      toAsset: toAsset,
    });
    return {
      mandateName: response.data.mandate_name,
      publicStatus: response.data.public_status,
      assets: response.data.assets.map((asset: AssetDetailResponseApi) => ({
        name: asset.name,
        amount: asset.amount,
        currentRate: asset.current_rate,
        percentage: asset.percentage,
        mandatePercentage: asset.mandate_percentage
      }))
    }
  }

  async placeLimitOrder(
    mandateId: number,
    fromAsset: string,
    amount: number,
    toAsset: string,
    limitPrice: number
  ): Promise<MandateTradingDataResponseApi> {
    const response = await api.post(`/trading/${mandateId}/limit-order`, {
      fromAsset: fromAsset,
      amount: amount,
      toAsset: toAsset,
      limitPrice: limitPrice
    });
    return {
      mandateName: response.data.mandate_name,
      publicStatus: response.data.public_status,
      assets: response.data.assets.map((asset: AssetDetailResponseApi) => ({
        name: asset.name,
        amount: asset.amount,
        currentRate: asset.current_rate,
        percentage: asset.percentage,
        mandatePercentage: asset.mandate_percentage
      }))
    }
  }

  async cancel_order(mandateId: number, orderId: string): Promise<void> {
    await api.delete(`/trading/${mandateId}/cancel/${orderId}`);
  }
}

export default new TradingMandateService();
